import { Button, Flex, Icon, Text } from '@chakra-ui/react';
import { BigNumber } from '@ethersproject/bignumber';
import { Card } from '@ui/components/Card';
import {
  ModalOrDrawerBody,
  ModalOrDrawerFooter,
} from '@ui/components/ModalOrDrawer';
import {
  TransactionError,
  TransactionPending,
} from '@ui/components/TransactionDrawer';
import { useCoinUsdValue } from '@ui/hooks/useCoinUsdValue';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import { useTranslations } from '@ui/i18n';
import { getGardenName } from '@ui/utils/garden';
import type { VaultType } from '@ui/views/Vno/ReservoirAndFountain/types';
import { CheckCircle } from '@veno-app/icons';
import React, { useMemo } from 'react';

import HarvestAllTokenInfo from './HarvestAllTokenInfo';
import { isGardenWithCoinMode, useHarvestAction } from './useHarvestAction';
import type { GardenWithCoinMode } from './useHarvestAllDrawer';
import {
  HarvestAllStep,
  useHarvestAllDrawer,
  useHarvestAllRewardsInfo,
} from './useHarvestAllDrawer';
import { getHarvestElementName } from './util';

function HarvestAllOverview() {
  const t = useTranslations();
  const {
    pendingRewardsMap,
    gardensHarvestable,
    fountainHarvestable,
    pendingRewardsFountain,
    pendingRewardsReservoir,
    reservoirHarvestable,
  } = useHarvestAllRewardsInfo();

  const {
    isTransactionPending,
    error,
    harvestedVaults,
    harvestedGardens,
    onClose,
    harvestedGardenRewards,
    harvestedFountainRewards,
    harvestedReservoirRewards,
  } = useHarvestAllDrawer();

  const gardensToDisplay = useMemo(() => {
    const harvestedGardensToInclude = harvestedGardens.filter(
      (hg) =>
        !gardensHarvestable
          .map((gh) => getGardenName(gh[0], gh[1]))
          .includes(getGardenName(hg[0], hg[1])),
    );
    return [...gardensHarvestable, ...harvestedGardensToInclude];
  }, [gardensHarvestable, harvestedGardens]);

  return !isTransactionPending && !error.code ? (
    <>
      <ModalOrDrawerBody pb="16px">
        <Flex gap={2} flexDir="column">
          {(reservoirHarvestable || harvestedVaults.includes('RESERVOIR')) && (
            <>
              <HarvestCard
                title={'Reservoir'}
                croRewards={
                  harvestedReservoirRewards?.wcro ??
                  pendingRewardsReservoir.wcro
                }
                atomRewards={
                  harvestedReservoirRewards?.atom ??
                  pendingRewardsReservoir.atom
                }
                element="RESERVOIR"
              />
            </>
          )}
          {(fountainHarvestable || harvestedVaults.includes('FOUNTAIN')) && (
            <HarvestCard
              title={'Fountain'}
              vnoRewards={
                harvestedFountainRewards ??
                pendingRewardsFountain ??
                BigNumber.from(0)
              }
              element="FOUNTAIN"
            />
          )}
          {gardensToDisplay.map((element, i) => {
            return (
              <HarvestCard
                vnoRewards={
                  harvestedGardenRewards[element[0]][element[1]]?.data ??
                  pendingRewardsMap[element[0]][element[1]].data
                }
                key={i}
                title={getHarvestElementName(element[1], element[0])}
                element={element}
              />
            );
          })}
        </Flex>
      </ModalOrDrawerBody>
      <ModalOrDrawerFooter>
        <Button
          w="full"
          onClick={() => {
            onClose();
          }}
        >
          {t('Done')}
        </Button>
      </ModalOrDrawerFooter>
    </>
  ) : error.code ? (
    <>
      {/* expected to be unlikely */}
      <ModalOrDrawerBody>
        <TransactionError.Content error={error} />
      </ModalOrDrawerBody>
      <ModalOrDrawerFooter>
        <Button
          w="full"
          onClick={() => {
            useHarvestAllDrawer.setState({ error: { code: '', message: '' } });
          }}
        >
          {t('Back')}
        </Button>
      </ModalOrDrawerFooter>
    </>
  ) : (
    <>
      <ModalOrDrawerBody>
        <TransactionPending.Content />
      </ModalOrDrawerBody>
    </>
  );
}

type HarvestRowProps = {
  title: string;
  vnoRewards?: BigNumber;
  croRewards?: BigNumber;
  atomRewards?: BigNumber;
  element: VaultType | GardenWithCoinMode | undefined;
};

const HarvestCard = ({
  title,
  vnoRewards,
  croRewards,
  element,
  atomRewards,
}: HarvestRowProps) => {
  const t = useTranslations();
  const croRewardsUsd = useCoinUsdValue(croRewards, 'cro').data;
  const atomRewardsUsd = useCoinUsdValue(atomRewards, 'atom').data;
  const vnoRewardsUsd = useVnoUsdValue(vnoRewards).data;
  const { mutateAsync: harvest } = useHarvestAction();
  const { harvestedGardens, harvestedVaults } = useHarvestAllDrawer();
  const elementIsGarden = isGardenWithCoinMode(element);

  const wasHarvested =
    (elementIsGarden &&
      harvestedGardens.some(
        (gardenWithCoinMode) =>
          gardenWithCoinMode[0] === element[0] &&
          gardenWithCoinMode[1] === element[1],
      )) ||
    harvestedVaults.includes(element as VaultType);

  return (
    <Card
      m={0}
      p={4}
      flexDir="column"
      display="flex"
      gap={3}
      shadow={wasHarvested ? 'unset' : 'card.normal'}
    >
      <Flex justify="space-between">
        <Text textStyle="h4Bold">{title}</Text>
        {!wasHarvested ? (
          <Button
            size="sm"
            onClick={
              elementIsGarden
                ? () => {
                    useHarvestAllDrawer.setState({
                      step: HarvestAllStep.HARVEST_GARDEN,
                      activeGarden: element,
                    });
                  }
                : () => harvest({ element })
            }
          >
            {t('Harvest')}
          </Button>
        ) : (
          <Flex align="center" gap={1} textStyle="ctaCopy" color="green.500">
            {t('Harvested')}
            <Icon w="24px" h="24px" as={CheckCircle} />
          </Flex>
        )}
      </Flex>
      {vnoRewards && (
        <HarvestAllTokenInfo
          title={t('{coin} rewards', {
            coin: 'VNO',
          })}
          usdRewards={vnoRewardsUsd}
          tokenRewards={vnoRewards}
          token="vno"
        />
      )}
      {croRewards && (
        <HarvestAllTokenInfo
          title={t('{coin} rewards', {
            coin: 'CRO',
          })}
          usdRewards={croRewardsUsd}
          tokenRewards={croRewards}
          token="cro"
        />
      )}
      {atomRewards && (
        <HarvestAllTokenInfo
          title={t('{coin} rewards', {
            coin: 'ATOM',
          })}
          usdRewards={atomRewardsUsd}
          tokenRewards={atomRewards}
          token="atom"
          tokenDecimals={6}
        />
      )}
    </Card>
  );
};

export default HarvestAllOverview;
